import { Controller } from "@hotwired/stimulus";

// Hover flyout nested menu with checkbox submenus (All Calibrate / per-group select).
export default class extends Controller {
  static targets = [
    "trigger",
    "mainMenu",
    "submenu",
    "hiddenInput",
    "summary",
    "checkbox",
    "form",
  ];

  static values = {
    emitHiddenChange: { type: Boolean, default: false },
    closeDelay: { type: Number, default: 120 },
    submitDebounce: { type: Number, default: 400 },
    reenableDelay: { type: Number, default: 500 },
    submenuOpenDelay: { type: Number, default: 60 },
    placeholder: { type: String, default: "Select" },
    arrayHiddenInputs: { type: Boolean, default: false },
  };

  connect() {
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.boundCheckboxKeydown = this.handleCheckboxKeydown.bind(this);
    this.boundFocusIn = this.handleFocusIn.bind(this);
    this.boundFocusOut = this.handleFocusOut.bind(this);
    this.boundCloseSubmenuOnScroll = this.closeSubmenuOnExternalScroll.bind(this);
    this.boundRepositionSubmenuOnResize = this.repositionActiveSubmenu.bind(this);
    this.boundTurboComplete = this.handleTurboComplete.bind(this);
    document.addEventListener("click", this.boundDocumentClick);
    document.addEventListener("keydown", this.boundDocumentKeydown);
    this.element.addEventListener("keydown", this.boundCheckboxKeydown);
    this.element.addEventListener("focusin", this.boundFocusIn);
    this.element.addEventListener("focusout", this.boundFocusOut);
    document.addEventListener("turbo:frame-load", this.boundTurboComplete);
    document.addEventListener("turbo:submit-end", this.boundTurboComplete);
    this.syncGroups();
    this.syncSelectAll();
    this.updateHiddenInput({ emitChange: false });
    this.updateSummary();
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick);
    document.removeEventListener("keydown", this.boundDocumentKeydown);
    this.element.removeEventListener("keydown", this.boundCheckboxKeydown);
    this.element.removeEventListener("focusin", this.boundFocusIn);
    this.element.removeEventListener("focusout", this.boundFocusOut);
    document.removeEventListener("turbo:frame-load", this.boundTurboComplete);
    document.removeEventListener("turbo:submit-end", this.boundTurboComplete);
    this.unbindSubmenuReposition();
    this.unbindMainMenuReposition();
    clearTimeout(this._closeMenuTimeout);
    clearTimeout(this._submitTimeout);
    clearTimeout(this._reenableTimeout);
    clearTimeout(this._openSubmenuTimeout);
    this._submitBaseline = null;
  }

  handleTurboComplete(event) {
    if (!this.isTurboEventForDropdown(event)) return;

    clearTimeout(this._reenableTimeout);
    this.enableAllCheckboxes();
  }

  isTurboEventForDropdown(event) {
    if (!this.hasFormTarget) return true;

    const form = this.formTarget;
    if (event.type === "turbo:submit-end") {
      return event.target === form;
    }

    const frame = event.target;
    if (frame?.tagName !== "TURBO-FRAME") return false;

    return (
      form.contains(frame) ||
      frame.contains(form) ||
      form === frame.closest("form")
    );
  }

  toggleMain(event) {
    event?.preventDefault();
    if (!this.hasMainMenuTarget) return;

    const opening = this.mainMenuTarget.classList.contains("hidden");
    if (opening) {
      this.openMain();
    } else {
      this.closeMainNow();
    }
  }

  openMain() {
    clearTimeout(this._closeMenuTimeout);
    if (!this.hasMainMenuTarget) return;

    this.mainMenuTarget.classList.add("is-fixed");
    this.mainMenuTarget.classList.remove("hidden");
    this.setMainExpanded(true);
    this.positionMainMenu();
    this.bindMainMenuReposition();
  }

  scheduleCloseMain(event) {
    const related = event?.relatedTarget;
    if (related && this.element.contains(related)) return;

    clearTimeout(this._closeMenuTimeout);
    this._closeMenuTimeout = setTimeout(() => {
      this.closeMainNow();
    }, this.closeDelayValue);
  }

  cancelCloseMain() {
    clearTimeout(this._closeMenuTimeout);
  }

  closeMainNow() {
    if (!this.hasMainMenuTarget) return;

    clearTimeout(this._openSubmenuTimeout);
    this._pendingSubmenuItem = null;
    this.mainMenuTarget.classList.add("hidden");
    this.setMainExpanded(false);
    this.closeAllSubmenus();
    this.clearMainMenuPosition();
    this.unbindMainMenuReposition();
    this.clearDropdownFocus();
  }

  openSubmenu(event) {
    const item = event.currentTarget;
    const submenu = item.querySelector(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (!submenu) return;

    const alreadyOpen =
      item.classList.contains("is-active") &&
      !submenu.classList.contains("hidden");
    if (alreadyOpen) return;

    // Hover-intent debounce avoids hit-test thrash between adjacent items.
    this._pendingSubmenuItem = item;
    clearTimeout(this._openSubmenuTimeout);
    this._openSubmenuTimeout = setTimeout(() => {
      if (this._pendingSubmenuItem !== item) return;
      this.commitOpenSubmenu(item);
    }, this.submenuOpenDelayValue);
  }

  commitOpenSubmenu(item) {
    const submenu = item.querySelector(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (!submenu) return;

    const alreadyOpen =
      item.classList.contains("is-active") &&
      !submenu.classList.contains("hidden");
    if (alreadyOpen) return;

    this.closeAllSubmenus({ clearFocus: false });
    submenu.classList.add("is-fixed");
    submenu.classList.remove("hidden");
    submenu.setAttribute("aria-hidden", "false");
    item.classList.add("is-active");
    this.setItemExpanded(item, true);
    this.positionSubmenu(submenu, item);
    this._activeSubmenuItem = item;
    this.bindSubmenuReposition();
  }

  closeSubmenu(event) {
    const item = event.currentTarget;
    const submenu = item.querySelector(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (!submenu) return;

    const related = event.relatedTarget;
    const relatedItem = related?.closest?.(".aqui-nested-dropdown__item");
    const relatedInMainMenu = !!(
      related &&
      this.hasMainMenuTarget &&
      this.mainMenuTarget.contains(related)
    );
    // Stay inside main menu: let openSubmenu / main leave handle switching.
    const skipped =
      !!(related && (submenu.contains(related) || item.contains(related))) ||
      relatedInMainMenu ||
      !!(relatedItem && relatedItem !== item);
    if (skipped) return;

    if (this._pendingSubmenuItem === item) {
      clearTimeout(this._openSubmenuTimeout);
      this._pendingSubmenuItem = null;
    }

    submenu.classList.add("hidden");
    submenu.setAttribute("aria-hidden", "true");
    item.classList.remove("is-active");
    this.setItemExpanded(item, false);
    this.clearSubmenuPosition(submenu);
    if (this._activeSubmenuItem === item) {
      this._activeSubmenuItem = null;
      this.unbindSubmenuReposition();
    }
  }

  toggleSubmenu(event) {
    let openedByKeyboard = false;

    if (event.type === "keydown") {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.classList?.remove("current-element");
        this.closeAllSubmenus({ clearFocus: false });
        this.focusWithoutScroll(this.triggerTarget);
        return;
      }
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openedByKeyboard = true;
    }

    const item = event.currentTarget.closest(".aqui-nested-dropdown__item");
    if (!item) return;

    const submenu = item.querySelector(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (!submenu) return;

    const opening = submenu.classList.contains("hidden");
    this.closeAllSubmenus({ clearFocus: false });
    if (!opening) return;

    submenu.classList.add("is-fixed");
    submenu.classList.remove("hidden");
    submenu.setAttribute("aria-hidden", "false");
    item.classList.add("is-active");
    this.setItemExpanded(item, true);
    this.positionSubmenu(submenu, item);
    this._activeSubmenuItem = item;
    this.bindSubmenuReposition();

    if (openedByKeyboard) {
      this.focusWithoutScroll(submenu.querySelector("input[type='checkbox']"));
    }
  }

  closeAllSubmenus({ clearFocus = true } = {}) {
    this.submenuTargets.forEach((submenu) => {
      submenu.classList.add("hidden");
      submenu.setAttribute("aria-hidden", "true");
      this.clearSubmenuPosition(submenu);
      const item = submenu.closest(".aqui-nested-dropdown__item");
      item?.classList.remove("is-active");
      this.setItemExpanded(item, false);
    });
    this._activeSubmenuItem = null;
    this.unbindSubmenuReposition();
    if (clearFocus) this.clearDropdownFocus();
  }

  positionSubmenu(submenu, item) {
    const rect = item.getBoundingClientRect();
    submenu.classList.add("is-fixed");

    const width = submenu.offsetWidth || 238; // Get submenu width, use default if not yet rendered
    const bridge = 2; // Pixels to slightly overlap parent item and submenu
    const edge = 8; // Minimum space to leave to screen edge
    const narrow = window.matchMedia?.("(max-width: 767px)")?.matches === true; // Mobile/small screen check

    // Calculate available space to left/right of submenu trigger item
    const spaceLeft = rect.left - edge;
    const spaceRight = window.innerWidth - rect.right - edge;
    const fitsLeft = spaceLeft >= width - bridge; // Is there enough space to left for submenu?
    const fitsRight = spaceRight >= width - bridge; // Is there enough space to right?

    let placement; // Placement direction: "left", "right", or "below"
    let left;
    let top = rect.top; // Default top position aligns submenu top with item top

    if (narrow || (!fitsLeft && !fitsRight)) {
      // Screen is narrow or neither side has space: show submenu below parent item
      placement = "below";
      top = rect.bottom; // Position below the item
      // Place left edge as close as possible to parent, keeping within window edges
      left = Math.max(
        edge,
        Math.min(rect.left, window.innerWidth - edge - width),
      );
    } else if (fitsLeft) {
      // Enough space to left: position submenu to left of parent item
      placement = "left";
      left = rect.left - width + bridge;
    } else {
      // Otherwise, position to right of parent item
      placement = "right";
      left = rect.right - bridge;
    }

    // Calculate max height for submenu based on placement and available space
    // For "below": fit below item but not less than 48px and not more than 220px
    // For "left"/"right": fit within viewport, leave space at top/bottom
    const maxH =
      placement === "below"
        ? Math.min(220, Math.max(48, window.innerHeight - top - edge))
        : Math.min(220, window.innerHeight - 16);

    // If submenu would overflow screen vertically, shift it upward
    if (placement !== "below" && top + maxH > window.innerHeight - edge) {
      top = Math.max(edge, window.innerHeight - edge - maxH);
    }

    submenu.style.top = `${top}px`;
    submenu.style.left = `${left}px`;
    submenu.style.maxHeight = `${maxH}px`;
  }

  positionMainMenu() {
    if (!this.hasMainMenuTarget || !this.hasTriggerTarget) return;

    const menu = this.mainMenuTarget;
    const triggerRect = this.triggerTarget.getBoundingClientRect();
    const gap = 4;
    const edge = 8;
    const preferredMax = 320;
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap - edge;
    const spaceAbove = triggerRect.top - gap - edge;
    const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
    const available = Math.max(120, openAbove ? spaceAbove : spaceBelow);
    const maxH = Math.min(preferredMax, available);

    menu.classList.add("is-fixed");
    menu.style.position = "fixed";
    menu.style.left = `${Math.max(edge, triggerRect.left)}px`;
    menu.style.width = `${Math.max(triggerRect.width, 180)}px`;
    menu.style.maxHeight = `${maxH}px`;
    menu.style.right = "auto";
    menu.style.marginTop = "0";

    if (openAbove) {
      menu.style.top = "auto";
      menu.style.bottom = `${window.innerHeight - triggerRect.top + gap}px`;
    } else {
      menu.style.top = `${triggerRect.bottom + gap}px`;
      menu.style.bottom = "auto";
    }
  }

  clearMainMenuPosition() {
    if (!this.hasMainMenuTarget) return;

    const menu = this.mainMenuTarget;
    menu.classList.remove("is-fixed");
    menu.style.position = "";
    menu.style.top = "";
    menu.style.bottom = "";
    menu.style.left = "";
    menu.style.right = "";
    menu.style.width = "";
    menu.style.maxHeight = "";
    menu.style.marginTop = "";
  }

  bindMainMenuReposition() {
    if (this._mainMenuRepositionBound) return;
    this._mainMenuRepositionBound = true;
    this.boundRepositionMainMenu = (event) => {
      if (this.mainMenuTarget?.classList.contains("hidden")) return;

      this.positionMainMenu();
      if (event?.type === "scroll") {
        this.closeSubmenuOnExternalScroll(event);
      } else {
        this.repositionActiveSubmenu();
      }
    };
    window.addEventListener("resize", this.boundRepositionMainMenu);
    window.addEventListener("scroll", this.boundRepositionMainMenu, true);
  }

  unbindMainMenuReposition() {
    if (!this._mainMenuRepositionBound) return;
    this._mainMenuRepositionBound = false;
    window.removeEventListener("resize", this.boundRepositionMainMenu);
    window.removeEventListener("scroll", this.boundRepositionMainMenu, true);
  }

  clearSubmenuPosition(submenu) {
    submenu.classList.remove("is-fixed");
    submenu.style.top = "";
    submenu.style.left = "";
    submenu.style.maxHeight = "";
  }

  closeSubmenuOnExternalScroll(event) {
    if (!this._activeSubmenuItem) return;

    const scrollTarget = event?.target;
    const submenu = this._activeSubmenuItem.querySelector(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (!submenu || submenu.classList.contains("hidden")) return;
    if (scrollTarget instanceof Element && this.element.contains(scrollTarget))
      return;
    if (submenu === scrollTarget || submenu.contains(scrollTarget)) return;

    this.closeAllSubmenus({ clearFocus: false });
  }

  repositionActiveSubmenu() {
    const item = this._activeSubmenuItem;
    if (!item) return;
    const submenu = item.querySelector(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (!submenu || submenu.classList.contains("hidden")) return;

    this.positionSubmenu(submenu, item);
  }

  bindSubmenuReposition() {
    if (this._submenuRepositionBound) return;
    this._submenuRepositionBound = true;
    document.addEventListener("scroll", this.boundCloseSubmenuOnScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", this.boundRepositionSubmenuOnResize);
  }

  unbindSubmenuReposition() {
    if (!this._submenuRepositionBound) return;
    this._submenuRepositionBound = false;
    document.removeEventListener("scroll", this.boundCloseSubmenuOnScroll, {
      capture: true,
    });
    window.removeEventListener("resize", this.boundRepositionSubmenuOnResize);
  }

  checkboxChanged(event) {
    const checkbox = event.target;
    if (checkbox.type !== "checkbox") return;

    const role = checkbox.dataset.role;

    if (role === "select_all") {
      this.toggleSelectAll(checkbox);
    } else if (role === "group") {
      this.toggleGroup(checkbox);
    } else if (role === "option") {
      this.syncGroupFor(checkbox.dataset.group);
    }

    this.syncSelectAll();
    this.updateHiddenInput({ emitChange: true });
    this.updateSummary();

    const focusCheckbox =
      document.activeElement?.type === "checkbox" &&
      this.element.contains(document.activeElement)
        ? document.activeElement
        : null;

    this.disableAllCheckboxes();
    clearTimeout(this._reenableTimeout);
    this._reenableTimeout = setTimeout(() => {
      this.enableAllCheckboxes(focusCheckbox);
    }, this.reenableDelayValue);
  }

  toggleSelectAll(checkbox) {
    const timing = checkbox.dataset.timing;
    if (!timing) return;

    this.checkboxTargets.forEach((cb) => {
      const group = cb.dataset.group || "";
      if (group.endsWith(`_${timing}`)) {
        cb.checked = checkbox.checked;
        cb.indeterminate = false;
        this.syncCheckboxAria(cb);
      }
    });
    this.syncGroups();
  }

  toggleGroup(checkbox) {
    const group = checkbox.dataset.group;
    if (!group) return;

    this.optionCheckboxesFor(group).forEach((cb) => {
      cb.checked = checkbox.checked;
      cb.indeterminate = false;
      this.syncCheckboxAria(cb);
    });
  }

  syncGroups() {
    const groups = new Set(
      this.checkboxTargets
        .filter((cb) => cb.dataset.role === "group" && cb.dataset.group)
        .map((cb) => cb.dataset.group),
    );
    groups.forEach((group) => this.syncGroupFor(group));
  }

  syncGroupFor(group) {
    if (!group) return;

    const groupbox = this.checkboxTargets.find(
      (cb) => cb.dataset.role === "group" && cb.dataset.group === group,
    );
    if (!groupbox) return;

    const options = this.optionCheckboxesFor(group);
    if (options.length === 0) {
      groupbox.checked = false;
      groupbox.indeterminate = false;
      this.syncCheckboxAria(groupbox);
      return;
    }

    const checkedCount = options.filter((cb) => cb.checked).length;
    if (checkedCount === options.length) {
      groupbox.checked = true;
      groupbox.indeterminate = false;
    } else if (checkedCount === 0) {
      groupbox.checked = false;
      groupbox.indeterminate = false;
    } else {
      groupbox.checked = false;
      groupbox.indeterminate = true;
    }
    this.syncCheckboxAria(groupbox);
  }

  syncSelectAll() {
    ["early", "late"].forEach((timing) => {
      const selectAll = this.checkboxTargets.find(
        (cb) =>
          cb.dataset.role === "select_all" && cb.dataset.timing === timing,
      );
      if (!selectAll) return;

      const groupboxes = this.checkboxTargets.filter(
        (cb) =>
          cb.dataset.role === "group" &&
          (cb.dataset.group || "").endsWith(`_${timing}`),
      );
      if (groupboxes.length === 0) {
        selectAll.checked = false;
        selectAll.indeterminate = false;
        this.syncCheckboxAria(selectAll);
        return;
      }

      const checkedCount = groupboxes.filter((cb) => cb.checked).length;
      const indeterminateCount = groupboxes.filter(
        (cb) => cb.indeterminate,
      ).length;

      if (checkedCount === groupboxes.length && indeterminateCount === 0) {
        selectAll.checked = true;
        selectAll.indeterminate = false;
      } else if (checkedCount === 0 && indeterminateCount === 0) {
        selectAll.checked = false;
        selectAll.indeterminate = false;
      } else {
        selectAll.checked = false;
        selectAll.indeterminate = true;
      }
      this.syncCheckboxAria(selectAll);
    });
  }

  optionCheckboxesFor(group) {
    return this.checkboxTargets.filter(
      (cb) => cb.dataset.role === "option" && cb.dataset.group === group,
    );
  }

  selectedOptionValues() {
    return this.checkboxTargets
      .filter(
        (cb) =>
          cb.dataset.role === "option" &&
          cb.checked &&
          cb.value &&
          cb.value !== "1",
      )
      .map((cb) => cb.value);
  }

  updateHiddenInput({ emitChange } = {}) {
    const values = this.selectedOptionValues();
    let previous = null;

    if (this.hasHiddenInputTarget) {
      previous = this.hiddenInputTarget.value;
      this.hiddenInputTarget.value = values.join(",");
    }

    if (this.arrayHiddenInputsValue) {
      this.updateArrayHiddenInputs(values);
    }

    if (!emitChange) return;

    clearTimeout(this._submitTimeout);
    if (this._submitBaseline == null) {
      this._submitBaseline = previous;
    }

    this._submitTimeout = setTimeout(() => {
      const live = this.hasHiddenInputTarget
        ? this.hiddenInputTarget.value
        : values.join(",");
      const baseline = this._submitBaseline;
      this._submitBaseline = null;

      if (live === baseline) return;

      if (this.hasHiddenInputTarget && this.emitHiddenChangeValue) {
        this.hiddenInputTarget.dispatchEvent(
          new Event("change", { bubbles: true }),
        );
      }
      if (this.hasFormTarget) {
        this.formTarget.requestSubmit();
      }
    }, this.submitDebounceValue);
  }

  updateArrayHiddenInputs(selectedIds) {
    this.element
      .querySelectorAll("input[type='hidden'][name='selected_item_ids[]']")
      .forEach((el) => el.remove());
    this.element
      .querySelectorAll("input[type='hidden'][name='no_items_selected']")
      .forEach((el) => el.remove());

    if (selectedIds.length === 0) {
      const noItems = document.createElement("input");
      noItems.type = "hidden";
      noItems.name = "no_items_selected";
      noItems.value = "1";
      this.element.appendChild(noItems);

      const empty = document.createElement("input");
      empty.type = "hidden";
      empty.name = "selected_item_ids[]";
      empty.value = "";
      this.element.appendChild(empty);
      return;
    }

    selectedIds.forEach((id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "selected_item_ids[]";
      input.value = id;
      this.element.appendChild(input);
    });
  }

  updateSummary() {
    if (!this.hasSummaryTarget) return;

    const values = this.selectedOptionValues();
    const labels = values.map((value) => {
      const cb = this.checkboxTargets.find(
        (box) => box.dataset.role === "option" && box.value === value,
      );
      const label = cb
        ?.closest("label")
        ?.querySelector(".aqui-nested-dropdown__checkbox-label");
      return label?.textContent?.trim() || value;
    });

    let text = this.placeholderValue;
    if (labels.length === 1) text = labels[0];
    else if (labels.length > 1) text = `${labels.length} selected`;

    this.summaryTarget.textContent = text;
    this.triggerTarget?.classList.toggle("is-filled", labels.length > 0);
  }

  disableAllCheckboxes() {
    this.element.querySelectorAll("input[type='checkbox']").forEach((cb) => {
      cb.disabled = true;
    });
  }

  enableAllCheckboxes(focusCheckbox = null) {
    this.element.querySelectorAll("input[type='checkbox']").forEach((cb) => {
      cb.disabled = false;
    });
    if (
      focusCheckbox &&
      !focusCheckbox.disabled &&
      document.body.contains(focusCheckbox)
    ) {
      this.focusWithoutScroll(focusCheckbox);
    }
  }

  focusWithoutScroll(element) {
    element?.focus({ preventScroll: true });
  }

  focusablePanelElements() {
    return [
      ...this.element.querySelectorAll(
        ".aqui-nested-dropdown__item-button:not([disabled]), input[type='checkbox']:not([disabled])",
      ),
    ].filter((element) => !element.closest(".hidden"));
  }

  movePanelFocus(event) {
    const focusables = this.focusablePanelElements();
    const index = focusables.indexOf(event.target);
    const nextIndex = event.key === "ArrowDown" ? index + 1 : index - 1;
    const next = index === -1 ? null : focusables[nextIndex];
    const nextInSubmenu = !!next?.closest?.(
      "[data-aquifer-ui-nested-dropdown-target='submenu']",
    );
    if (index === -1) return;

    event.preventDefault();
    if (!next) return;

    if (!nextInSubmenu) {
      this.closeAllSubmenus({ clearFocus: false });
    }
    this.focusWithoutScroll(next);
  }

  handleCheckboxKeydown(event) {
    if (event.target.type === "checkbox") {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        this.movePanelFocus(event);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        event.target.classList.remove("current-element");
        this.dismissSubmenuToParentItem(event.target);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.target.checked = !event.target.checked;
        event.target.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    if (
      event.target.classList?.contains("aqui-nested-dropdown__item-button") &&
      (event.key === "ArrowDown" || event.key === "ArrowUp")
    ) {
      this.movePanelFocus(event);
      return;
    }

    if (event.key !== "Escape") return;
    if (event.target.classList?.contains("aqui-nested-dropdown__item-button")) return;
    if (!this.hasMainMenuTarget || this.mainMenuTarget.classList.contains("hidden")) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeMainNow();
    this.focusWithoutScroll(this.triggerTarget);
  }

  dismissSubmenuToParentItem(fromElement) {
    const item = fromElement.closest(".aqui-nested-dropdown__item");
    const button = item?.querySelector(".aqui-nested-dropdown__item-button");
    this.closeAllSubmenus({ clearFocus: false });
    this.focusWithoutScroll(button || this.triggerTarget);
  }

  handleFocusIn(event) {
    if (event.target.type === "checkbox") {
      event.target.classList.add("current-element");
    }
    if (
      event.target.classList?.contains("aqui-nested-dropdown__item-button") &&
      this.submenuTargets.some((submenu) => !submenu.classList.contains("hidden"))
    ) {
      this.closeAllSubmenus({ clearFocus: false });
    }
  }

  handleFocusOut(event) {
    if (event.target.type === "checkbox") {
      event.target.classList.remove("current-element");
    }
  }

  clearDropdownFocus() {
    this.element.querySelectorAll(".current-element").forEach((el) => {
      el.classList.remove("current-element");
    });
    const active = document.activeElement;
    if (!active || !this.element.contains(active) || active === this.triggerTarget)
      return;

    this.focusWithoutScroll(this.triggerTarget);
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target)) return;
    this.closeMainNow();
  }

  handleDocumentKeydown(event) {
    if (event.key !== "Escape") return;
    if (this.element.contains(event.target)) return;
    if (this.mainMenuTarget?.classList.contains("hidden")) return;

    event.preventDefault();
    this.closeMainNow();
    this.focusWithoutScroll(this.triggerTarget);
  }

  setMainExpanded(expanded) {
    this.triggerTarget?.setAttribute(
      "aria-expanded",
      expanded ? "true" : "false",
    );
    this.mainMenuTarget?.setAttribute(
      "aria-hidden",
      expanded ? "false" : "true",
    );
  }

  setItemExpanded(item, expanded) {
    const button = item?.querySelector(".aqui-nested-dropdown__item-button");
    button?.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  syncCheckboxAria(checkbox) {
    if (!checkbox || checkbox.type !== "checkbox") return;

    if (checkbox.indeterminate) {
      checkbox.setAttribute("aria-checked", "mixed");
    } else {
      checkbox.setAttribute(
        "aria-checked",
        checkbox.checked ? "true" : "false",
      );
    }
  }
}
